import * as Tabs from '@radix-ui/react-tabs'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Pause, Play, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AgreementsTab } from '@/components/recurring/AgreementsTab'
import { ContentTab } from '@/components/recurring/ContentTab'
import { DistributionTab } from '@/components/recurring/DistributionTab'
import { FREQ_LABEL, ScheduleCard } from '@/components/recurring/ScheduleCard'
import type { ScheduleForm } from '@/components/recurring/ScheduleCard'
import { Card } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { SlidingTabsList, slidingTabTrigger } from '@/components/ui/SlidingTabsList'
import { Tooltip } from '@/components/ui/Tooltip'
import { useIsAdmin } from '@/lib/auth'
import { toNumber } from '@/lib/decimal'
import { useBeneficiaries, useDeleteRecurring, useRecurringActions, useRecurringDetail } from '@/lib/recurring-api'
import { groupsFromDetail, groupsProblems, toGroupInputs } from '@/lib/recurring-draft'
import type { DraftProblem, GroupDraft } from '@/lib/recurring-draft'
import type { RecurringDeletionInfo, RecurringDetail } from '@/lib/recurring-types'
import { cn } from '@/lib/utils'

type TabValue = 'content' | 'distribution' | 'agreements'

/** Vergelijkbare vingerafdruk van de draft, om onopgeslagen werk te herkennen. */
function draftKey(groups: GroupDraft[], schedule: ScheduleForm): string {
  return JSON.stringify([toGroupInputs(groups), schedule])
}

function scheduleFromDetail(detail: RecurringDetail): ScheduleForm {
  return {
    name: detail.name,
    subjectTemplate: detail.subjectTemplate,
    frequency: detail.frequency,
    intervalCount: String(detail.intervalCount),
    nextIssueDate: detail.nextIssueDate ?? '',
    leadTimeDays: String(detail.leadTimeDays),
    endDate: detail.endDate ?? '',
    indexed: detail.indexed,
    indexationMonth: String(detail.indexationMonth ?? 1),
  }
}

/** Waarom de verwijder-knop uit staat; null zolang er niets blokkeert. */
function deletionBlockedReason(deletion: RecurringDeletionInfo): string | null {
  if (!deletion.blocked) return null
  const invoiceCount = deletion.invoiceCount
  const invoicePart = invoiceCount > 0 ? `${invoiceCount} ${invoiceCount === 1 ? 'factuur is' : 'facturen zijn'} al uit deze reeks gegenereerd` : null
  const indexationPart = deletion.indexationApplied ? 'er is al indexatie toegepast op deze reeks' : null
  const reason = [invoicePart, indexationPart].filter(Boolean).join(' en ')
  return `Kan niet verwijderd worden: ${reason}. Zet de reeks op non-actief in plaats daarvan.`
}

export function RecurringDetailPage({ recurringId }: { recurringId: string }) {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const detailQuery = useRecurringDetail(recurringId)
  const beneficiariesQuery = useBeneficiaries()
  const actions = useRecurringActions(recurringId)
  const deleteRecurring = useDeleteRecurring()
  const detail = detailQuery.data

  const search = useSearch({ strict: false }) as { tab?: string }
  const tab = (search.tab === 'distribution' || search.tab === 'agreements' ? search.tab : 'content') as TabValue

  const [schedule, setSchedule] = useState<ScheduleForm | null>(null)
  const [groups, setGroups] = useState<GroupDraft[] | null>(null)
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const seededRef = useRef<string | null>(null)

  /**
   * De draft volgt de server zolang er niets onopgeslagen is. Dat is niet
   * cosmetisch: indexatie herschrijft de bedragen server-side, en een draft die
   * daarna blijft staan zou die verhoging bij de eerstvolgende opslag
   * terugdraaien zonder dat hij opnieuw toegepast kan worden. Heeft de gebruiker
   * wel wijzigingen openstaan, dan blijven die staan.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: alleen verse serverdata mag reseeden; groups en schedule zijn de lokale draft en zouden het effect bij elke toetsaanslag laten lopen
  useEffect(() => {
    if (!detail) return
    const freshGroups = groupsFromDetail(detail)
    const freshSchedule = scheduleFromDetail(detail)
    const freshKey = draftKey(freshGroups, freshSchedule)
    const currentKey = groups && schedule ? draftKey(groups, schedule) : null

    if (currentKey === freshKey) {
      seededRef.current = freshKey
      return
    }
    if (currentKey === null || currentKey === seededRef.current) {
      setGroups(freshGroups)
      setSchedule(freshSchedule)
      seededRef.current = freshKey
    }
  }, [detail])

  function setTab(next: TabValue) {
    navigate({
      to: '/terugkerend/$id',
      params: { id: recurringId },
      search: (prev) => ({ ...prev, tab: next === 'content' ? undefined : next }),
      replace: true,
    })
  }

  if (detailQuery.isError) {
    return <ErrorState error={detailQuery.error} onRetry={() => detailQuery.refetch()} />
  }
  if (!detail || !schedule || !groups) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const problems = groupsProblems(groups)
  const contentProblem = problems.find((p) => p.scope === 'content') ?? null
  const distributionProblem = problems.find((p) => p.scope === 'distribution') ?? null
  const scheduleProblem = schedule.name.trim() === '' || schedule.subjectTemplate.trim() === '' || schedule.nextIssueDate === ''
  const blocking: DraftProblem | null = contentProblem ?? distributionProblem
  const openComments = detail.comments.filter((c) => c.resolvedAt === null).length

  const canSave = isAdmin && problems.length === 0 && !scheduleProblem && !actions.update.isPending
  const deleteBlockedReason = deletionBlockedReason(detail.deletion)

  const save = () =>
    actions.update.mutate({
      name: schedule.name.trim(),
      subjectTemplate: schedule.subjectTemplate.trim(),
      frequency: schedule.frequency,
      intervalCount: Math.max(1, Math.round(toNumber(schedule.intervalCount)) || 1),
      nextIssueDate: schedule.nextIssueDate || null,
      leadTimeDays: Math.max(0, Math.round(toNumber(schedule.leadTimeDays))),
      endDate: schedule.endDate || null,
      indexed: schedule.indexed,
      indexationMonth: schedule.indexed ? Number(schedule.indexationMonth) || null : null,
      groups: toGroupInputs(groups),
    })

  const openDistribution = (lineKey: string) => {
    setFocusKey(lineKey)
    setTab('distribution')
  }

  return (
    <div className="space-y-6 pb-20">
      <PageHeader
        title={detail.name}
        description={`${detail.clientName} · ${FREQ_LABEL[detail.frequency]}${detail.active ? '' : ' · gepauzeerd'}`}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/terugkerend" className="btn-ghost h-9 px-2.5 text-sm">
              <ArrowLeft className="h-4 w-4" /> Reeksen
            </Link>
            {isAdmin ? (
              <>
                <button type="button" className="btn-secondary" onClick={() => actions.setActive.mutate(!detail.active)}>
                  {detail.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {detail.active ? 'Pauzeren' : 'Hervatten'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => actions.generateNow.mutate()} disabled={actions.generateNow.isPending}>
                  <Sparkles className="h-4 w-4" /> Nu genereren
                </button>
                {deleteBlockedReason ? (
                  <Tooltip content={deleteBlockedReason}>
                    <span tabIndex={0} className="inline-flex">
                      <button type="button" className="btn-secondary text-status-overdue hover:text-status-overdue" disabled>
                        <Trash2 className="h-4 w-4" /> Verwijder
                      </button>
                    </span>
                  </Tooltip>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary text-status-overdue hover:text-status-overdue"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" /> Verwijder
                  </button>
                )}
              </>
            ) : null}
          </div>
        }
      />

      {confirmDelete ? (
        <Modal
          open
          onOpenChange={(o) => !o && setConfirmDelete(false)}
          title="Reeks verwijderen"
          description="Deze reeks wordt definitief verwijderd. Dit kan alleen als er nog geen facturen uit gegenereerd zijn en er geen indexatie op is toegepast."
          footer={
            <>
              <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(false)}>Annuleren</button>
              <button
                type="button"
                className="btn-primary bg-status-overdue hover:bg-status-overdue/90"
                onClick={() =>
                  deleteRecurring.mutate(recurringId, { onSuccess: () => navigate({ to: '/terugkerend' }) })
                }
                disabled={deleteRecurring.isPending}
              >
                <Trash2 className="h-4 w-4" />
                {deleteRecurring.isPending ? 'Verwijderen…' : 'Verwijder'}
              </button>
            </>
          }
        >
          <p className="text-sm text-ink-soft">Weet je het zeker?</p>
        </Modal>
      ) : null}

      {detail.indexationPending ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-hairline bg-fill-1 px-4 py-2.5 text-sm">
          <span className="h-2 w-2 shrink-0 rounded-full bg-status-overdue" />
          <span className="text-ink">De indexatie van deze reeks staat nog open, dus er wordt geen concept gegenereerd.</span>
          <button type="button" className="btn-ghost ml-auto h-7 px-2 text-sm" onClick={() => setTab('agreements')}>
            Naar indexatie
          </button>
        </div>
      ) : null}

      <ScheduleCard
        schedule={schedule}
        onChange={(patch) => setSchedule((prev) => (prev ? { ...prev, ...patch } : prev))}
        readonly={!isAdmin}
        periodLabel={detail.nextPeriodLabel}
      />

      <Tabs.Root value={tab} onValueChange={(v) => setTab(v as TabValue)}>
        <SlidingTabsList value={tab}>
          <Tabs.Trigger value="content" className={cn(slidingTabTrigger, 'inline-flex items-center gap-1.5')}>
            Inhoud
            {contentProblem ? <ProblemDot /> : null}
          </Tabs.Trigger>
          <Tabs.Trigger value="distribution" className={cn(slidingTabTrigger, 'inline-flex items-center gap-1.5')}>
            Verdeling
            {distributionProblem ? <ProblemDot /> : null}
          </Tabs.Trigger>
          <Tabs.Trigger value="agreements" className={cn(slidingTabTrigger, 'inline-flex items-center gap-1.5')}>
            Afspraken
            {openComments > 0 ? (
              <span className="num rounded-full bg-fill-2 px-1.5 text-xs text-ink-faint">{openComments}</span>
            ) : null}
          </Tabs.Trigger>
        </SlidingTabsList>

        <Tabs.Content value="content" className="mt-4">
          <Card>
            <ContentTab groups={groups} onChange={setGroups} onOpenDistribution={openDistribution} readonly={!isAdmin} />
          </Card>
        </Tabs.Content>
        <Tabs.Content value="distribution" className="mt-4">
          <DistributionTab
            groups={groups}
            onChange={setGroups}
            beneficiaries={beneficiariesQuery.data ?? []}
            focusKey={focusKey}
            onFocusHandled={() => setFocusKey(null)}
            readonly={!isAdmin}
          />
        </Tabs.Content>
        <Tabs.Content value="agreements" className="mt-4">
          <AgreementsTab
            detail={detail}
            isAdmin={isAdmin}
            onLinesRewritten={(fresh) => {
              const freshGroups = groupsFromDetail(fresh)
              const freshSchedule = scheduleFromDetail(fresh)
              setGroups(freshGroups)
              setSchedule(freshSchedule)
              seededRef.current = draftKey(freshGroups, freshSchedule)
            }}
          />
        </Tabs.Content>
      </Tabs.Root>

      {isAdmin ? (
        <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-3 border-t border-hairline bg-canvas/85 px-4 py-3 backdrop-blur-xl lg:-mx-8 lg:px-8">
          {scheduleProblem ? (
            <span className="text-sm text-status-overdue">Vul bij Schema de naam, het onderwerp en de volgende factuurdatum in.</span>
          ) : blocking ? (
            blocking.scope === tab ? (
              <span className="text-sm text-status-overdue">{blocking.message}</span>
            ) : (
              <button
                type="button"
                className="btn-ghost h-8 text-sm text-status-overdue"
                onClick={() => setTab(blocking.scope === 'content' ? 'content' : 'distribution')}
              >
                {blocking.message}
              </button>
            )
          ) : null}
          <button type="button" className="btn-primary" onClick={save} disabled={!canSave}>
            Opslaan
          </button>
        </div>
      ) : null}
    </div>
  )
}

function ProblemDot() {
  return (
    <>
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-status-overdue" />
      <span className="sr-only">bevat een probleem</span>
    </>
  )
}
